import { Seo } from './seo';
import { RequisitesCard } from './requisites-card';
import { useLocale, useT } from '../i18n/locale-context';
import type { LegalBlock, LegalDoc } from '../content/legal';
import { legalDocLocales } from '../lib/legal-locales';

function Block({ block }: { block: LegalBlock }) {
  if (typeof block === 'string') {
    return <p className="text-[14px] leading-[1.7] text-ink-soft">{block}</p>;
  }
  return (
    <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-[1.65] text-ink-soft marker:text-brand">
      {block.list.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * Renders a structured legal document (оферта / privacy) in the m16 style.
 * Title is localised (uz/ru). The body is Russian unless the document carries
 * an Uzbek one (`doc.uz`) and we are on /uz — the Russian version prevails in
 * a dispute, which both bodies state. Anything without `doc.uz` (the KZ set)
 * renders exactly as before. The company requisites are appended so the
 * offer/privacy are self-contained per the e-commerce disclosure rules.
 */
export function LegalPage({ doc, canonical }: { doc: LegalDoc; canonical: string }) {
  const t = useT();
  const { locale } = useLocale();
  const title = doc.title[locale] ?? doc.title.ru;
  const body = locale === 'uz' && doc.uz ? doc.uz : doc;
  const updated = (locale === 'uz' && doc.updatedUz) || doc.updated;
  // A reader in a locale the document is not written in (/kk/offer: the body is
  // the Russian one) gets a page that describes itself as that Russian page —
  // `<html lang>`, canonical and hreflang point at the ru URL, as on the
  // borrowed-body guide pages. See `legalDocLocales`.
  const ownLocales = legalDocLocales(doc);
  const pageLang = ownLocales.includes(locale) ? locale : (ownLocales[0] ?? locale);

  return (
    <div className="mx-auto max-w-[820px]">
      <Seo
        title={title}
        description={body.intro[0] as string}
        canonical={canonical}
        lang={pageLang}
        locales={ownLocales}
      />

      <h1 className="mb-1 font-brand text-[24px] font-extrabold tracking-[-0.3px] text-ink">
        {title}
      </h1>
      <p className="mb-6 text-[12.5px] text-ink-muted">
        {t('legal.updated')}: {updated}
      </p>

      <div className="flex flex-col gap-3">
        {body.intro.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-8">
        {body.sections.map((section) => (
          <section key={section.heading} className="flex flex-col gap-3">
            <h2 className="text-[16px] font-bold text-ink">{section.heading}</h2>
            {section.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </section>
        ))}
      </div>

      <h2 className="mb-3 mt-10 text-[16px] font-bold text-ink">{t('legal.requisitesHeading')}</h2>
      <RequisitesCard />
    </div>
  );
}
